import { IFloorRequest } from "../../../models/floor.model";
import { formatToDDMMYYYY } from "../../../utils/formatDateUtils";
import PaginationControls from "../../../utils/PaginationControls";
import FloorRequestStatusBadge from "./FloorStatusBadge";
import { Inbox } from "lucide-react";

interface NewFloorRequestsPageProps {
  requests: IFloorRequest[];
  loading: boolean;
  pagination: any;
  requestTab: "pending" | "approved" | "rejected" | "cancelled";
  floorRequestpage: number;
  setFloorRequestPage: (page: number) => void;
  onRequestTabChange: (
    status: "pending" | "approved" | "rejected" | "cancelled",
  ) => void;
  onSelectRequest: (request: IFloorRequest) => void;
}

export default function NewFloorRequestsPage({
  requests,
  loading,
  pagination,
  requestTab,
  floorRequestpage,
  setFloorRequestPage,
  onRequestTabChange,
  onSelectRequest,
}: NewFloorRequestsPageProps) {
  // Helper function to extract name from either productId or rawMaterialId
  const getItemName = (row: any) => {
    return row?.productId?.productName || row?.rawMaterialId?.name || null;
  };

  return (
    <>
      {/* Sub Tabs Options Selector */}
      <div className="flex flex-wrap gap-2 p-4 md:p-5 border-b bg-slate-50">
        {(["pending", "approved", "rejected", "cancelled"] as const).map(
          (status) => (
            <button
              key={status}
              onClick={() => onRequestTabChange(status)}
              className={`rounded-lg px-4 py-2 text-sm font-medium capitalize transition ${
                requestTab === status
                  ? "bg-indigo-600 text-white shadow"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
            >
              {status}
            </button>
          ),
        )}
      </div>

      {/* Dynamic Main Body Content */}
      <div className="p-4 md:p-6">
        {loading ? (
          /* Loading State skeleton container */
          <div className="py-12 flex flex-col items-center justify-center space-y-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
            <p className="text-slate-500 font-medium text-sm">
              Loading requests...
            </p>
          </div>
        ) : requests.length === 0 ? (
          /* Handled Empty Requests State View */
          <div className="py-16 flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <div className="p-4 bg-slate-100 rounded-full text-slate-400 mb-3">
              <Inbox size={32} />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">
              No Requests Found
            </h3>
            <p className="text-slate-400 text-sm max-w-xs text-center mt-1">
              There are currently no {requestTab} inventory floor items requests
              available matching this view.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop View Table: Hidden on Mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 font-semibold text-sm">
                    <th className="p-3">Request</th>
                    <th className="p-3">Items</th>
                    <th className="p-3">Requested By</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {requests.map((row) => {
                    const itemName = getItemName(row);
                    return (
                      <tr
                        key={row._id}
                        className="hover:bg-indigo-50/40 cursor-pointer transition"
                        onClick={() => onSelectRequest(row)}
                      >
                        <td className="p-3 font-semibold text-slate-900">
                          #REQ-{row.requestNo}{" "}
                          {itemName ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200">
                              {itemName}
                            </span>
                          ) : null}
                        </td>
                        <td className="p-3">{row.items || 0}</td>
                        <td className="p-3 text-sm">
                          {row.requestedBy?.username || "N/A"}
                        </td>
                        <td className="p-3 text-sm">
                          {formatToDDMMYYYY(row.createdAt)}
                        </td>
                        <td className="p-3">
                          <FloorRequestStatusBadge status={row.status} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile View Layout Cards stacked list: Hidden on Desktop */}
            <div className="block md:hidden space-y-3">
              {requests.map((row) => {
                const itemName = getItemName(row);
                return (
                  <div
                    key={row._id}
                    onClick={() => onSelectRequest(row)}
                    className="p-4 rounded-xl border border-slate-200 bg-white hover:border-indigo-300 active:scale-[0.99] transition shadow-sm space-y-3 cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">
                        #REQ-{row.requestNo}
                      </span>
                      <FloorRequestStatusBadge status={row.status} />
                    </div>

                    {itemName && (
                      <div>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                          {itemName}
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-y-2 text-sm border-t border-slate-100 pt-2 text-slate-500">
                      <div>
                        <span className="block text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Items Count
                        </span>
                        <span className="font-medium text-slate-800">
                          {row.items || 0} items
                        </span>
                      </div>
                      <div>
                        <span className="block text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Requested By
                        </span>
                        <span className="font-medium text-slate-800 truncate block">
                          {row.requestedBy?.username || "N/A"}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="block text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Date Created
                        </span>
                        <span className="font-medium text-slate-800">
                          {formatToDDMMYYYY(row.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Responsive Centered Pagination Controls Spacer Wrapper */}
            <div className="mt-6 pt-4 border-t border-slate-100">
              <PaginationControls
                currentPage={floorRequestpage}
                totalPages={
                  pagination.getFloorRequestsPagination?.totalPages || 0
                }
                onPageChange={setFloorRequestPage}
                isLoading={loading}
                totalItems={pagination.getFloorRequestsPagination?.totalCount}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
